import { nav, stores } from '@stores'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import includes from 'lodash/includes'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getChannelName } from '@utils/get-channel-name'

export function useProductStatus(product) {
  const { clientType, clientId } = nav.queryParams
  if (includes(product?.excludedClients, clientId)) {
    return false
  } else if (
    clientType === getEnumValue(VIPClientType.External) &&
    !includes(product?.channels, VIPChannel.Wholesale)
  ) {
    return false
  } else if (
    clientType === getEnumValue(VIPClientType.Internal) &&
    !includes(product?.channels, VIPChannel.Retail)
  ) {
    return false
  } else if (product?.allClustersExcluded) {
    return false
  } else if (product?.original?.active) {
    return product?.original?.active
  } else {
    return false
  }
}

/**
 * This function returns the product status
 * @param excludedInDoors
 * @param doorOrders
 * @param checked if true means book all, else for unBook all
 * @returns
 */
export function useEnhancedProductStatus(product) {
  const {
    nav: {
      queryParams: { clientType, clientId }
    }
  } = stores
  return checkEnhancedProductActiveStatus(product, clientType, clientId)
}

export function useEnhancedProducDetailsStatus(product) {
  if (!product?.id) {
    // product data is not loaded yet so do not show anything
    return true
  }
  const {
    nav: {
      queryParams: { clientType, clientId }
    }
  } = stores
  return checkEnhancedProductActiveStatus(product, clientType, clientId)
}

export const checkEnhancedProductActiveStatus = (product, clientType, clientId) => {
  if (!product) {
    return false
  }
  const isChannelIncluded = client => {
    const isClType = clientType === getEnumValue(client)
    const includesChannel = !includes(
      product?.channels?.map(el => el.toLowerCase()),
      getChannelName(getEnumValue(client)).toLowerCase()
    )
    return isClType && includesChannel
  }

  if (includes(product?.['excludedClients-id'], clientId)) {
    return false
  } else if (isChannelIncluded(VIPClientType.External)) {
    return false
  } else if (isChannelIncluded(VIPClientType.Internal)) {
    return false
  } else if (product?.allClustersExcluded) {
    return false
  } else if (product?.['original-active']) {
    return product?.['original-active']
  } else {
    return false
  }
}

/**
 * to determine wether the cluster is excluded or not
 * @param excludedInDoors
 * @param doorOrders
 * @param doorOrderId
 * @returns boolean
 */
export function useExclusionDoor(
  excludedInDoors = [],
  doorOrders = [],
  doorOrderId: string
) {
  const doorObj = doorOrders?.find(obj => obj?.id === doorOrderId)
  return includes(excludedInDoors, doorObj?.door?.id)
}

/**
 * To pass only the excluded door IDs when the user book all or unBook all
 * @param excludedInDoors
 * @param doorOrders
 * @param checked if true means book all, else for unBook all
 * @returns
 */
export function getUnExcludedDoorIds(
  excludedInDoors = [],
  doorOrders = [],
  checked = false
) {
  const doorIds = doorOrders?.map(doorOrder => doorOrder?.id)
  if (!checked) return doorIds // This is for Un book All, If items are booked then user can do all operation in UnBook
  const tempIds = []
  doorOrders?.forEach(obj => {
    if (!excludedInDoors?.includes(obj?.door?.id)) tempIds.push(obj?.id)
  })
  return tempIds
}

export const getUnExcludedDoors = (
  excludedInDoors = [],
  doorOrders = [],
  checked = false
) => {
  const doorIds = doorOrders?.map(doorOrder => doorOrder?.door?.id)
  if (!checked) return doorIds // This is for Un book All, If items are booked then user can do all operation in UnBook
  const tempIds = []
  doorOrders?.forEach(obj => {
    if (!excludedInDoors?.includes(obj?.door?.id)) tempIds.push(obj?.door?.id)
  })
  return tempIds
}

/**
 * To pass only the excluded door IDs when the user book all or unBook all
 * @param excludedInDoors
 * @param doorOrders
 * @param bookedDoorsIDs if true means book all, else for unBook all
 * @returns
 */
export function checkWhetherBookedOrNot(
  excludedInDoors = [],
  doorOrders = [],
  bookedDoorsIDs = [],
  isEnhancedViewEnabled = false
) {
  const tempIds = []
  doorOrders?.forEach(obj => {
    if (
      excludedInDoors?.includes(obj?.door?.id) &&
      !(isEnhancedViewEnabled
        ? bookedDoorsIDs?.includes(obj?.door?.id)
        : bookedDoorsIDs?.includes(obj?.id))
    )
      tempIds.push(obj?.id)
  })

  return bookedDoorsIDs.length
    ? tempIds.length + bookedDoorsIDs.length === doorOrders.length
    : false
}

/**
 *
 * @param excludedInDoors
 * @param doorOrders
 * @returns getExcludedClusters
 */
export function getExcludedClusters(excludedInDoors = [], doorOrders = []) {
  if (!excludedInDoors.length) return []

  const cluster = []
  doorOrders?.forEach(doorOrder => {
    if (excludedInDoors?.includes(doorOrder?.door?.id)) {
      cluster.push(doorOrder?.door?.cluster?.name)
    }
  })
  return cluster
}

export const useProductQuantityHighlight = (productQuantity, doorQuantity) => {
  const {
    productConfiguration: { featureFlags, isWarningModeActive }
  } = stores
  if (isWarningModeActive) {
    if (!productQuantity) {
      return true
    }
    return false
  }
  return featureFlags?.highlightQuantityDiscrepancies && productQuantity !== doorQuantity
}

export const useDoorQuantityHighlight = (
  isBooked,
  doorQuantity = 0,
  sizeQuantity = 0
) => {
  if (!isBooked) {
    return false
  }
  const {
    productConfiguration: { featureFlags, isWarningModeActive }
  } = stores
  if (isWarningModeActive) {
    if (!doorQuantity) {
      return true
    }
    return false
  }

  return featureFlags?.highlightQuantityDiscrepancies && doorQuantity !== sizeQuantity
}

export const productStatusForWarning = (isWarningModeActive, attributes, product) => {
  if (!isWarningModeActive || !attributes.length || !product) {
    return false
  }
  return attributes.some(attribute => {
    return attribute.filterValues.includes(product?.[attribute.filterName])
  })
}

export const useProductStatusForWarning = (attributes, product) => {
  const {
    productConfiguration: { isWarningModeActive }
  } = stores

  return productStatusForWarning(isWarningModeActive, attributes, product)
}
